export function fmtKg(v: number | null | undefined, digits = 1): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toFixed(digits)} kg`;
}

export function fmtKcal(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Math.round(v).toLocaleString()} kcal`;
}

export function fmtNumber(v: number | null | undefined, digits = 0): string {
  if (v == null || Number.isNaN(v)) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: digits });
}

/**
 * A moment, as briefly as it can be said without losing the day: "7:12 pm"
 * today, "Yesterday 7:12 pm", "Mon 14 Sep, 7:12 pm" otherwise. A bare time
 * for anything older than today made weeks-old weigh-ins read as this morning.
 */
export function fmtTimeShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const dayKey = (x: Date) => x.toDateString();
  const now = new Date();
  if (dayKey(d) === dayKey(now)) return time;
  if (dayKey(d) === dayKey(new Date(now.getTime() - 864e5))) return `Yesterday ${time}`;
  const opts: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return `${d.toLocaleDateString([], opts)}, ${time}`;
}

/** Whole days between a moment and now, for "3 days ago". */
export function daysAgo(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
}

/** Minutes as "39:12" under an hour, "1:05:30" over. */
export function fmtDuration(min: number | null | undefined): string {
  if (min == null || Number.isNaN(min)) return "—";
  const total = Math.round(min * 60);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Pace per km, from average speed or from distance and time. */
export function fmtPace(opts: { mps?: number | null; km?: number | null; min?: number | null }): string {
  let secPerKm: number | null = null;
  if (opts.mps && opts.mps > 0) secPerKm = 1000 / opts.mps;
  else if (opts.km && opts.km > 0 && opts.min) secPerKm = (opts.min * 60) / opts.km;
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm > 60 * 60) return "—";
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return s === 60 ? `${m + 1}:00 /km` : `${m}:${String(s).padStart(2, "0")} /km`;
}

/** A percentage to one decimal: the scale sends 27.269000000000002. */
export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v == null || Number.isNaN(v)) return "—";
  return v.toFixed(digits);
}

export function fmtKm(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v.toFixed(v < 10 ? 2 : 1)} km`;
}

/** Sleep minutes as "7h 12m". */
export function fmtHours(min: number | null | undefined): string {
  if (min == null || Number.isNaN(min)) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

export function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}
